import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Drawer, FormControlLabel, IconButton, LinearProgress, MenuItem, Switch, TextField, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { StatusChip } from '../../components/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../contexts/ToastContext';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { tokens } from '../../theme/tokens';
import { asDate, systemJson } from './api';
import { afterOptionChange, canApply, classifyCompletion, clearReview, finishApply, finishDryRun, RunPhase } from './runGate';

export interface ScriptOption {
  key: string;
  label: string;
  type: 'text' | 'boolean' | 'select';
  defaultValue: string | boolean;
  help?: string;
  choices?: Array<{ value: string; label: string }>;
}

export interface SampleRow {
  id: string;
  title: string;
  before?: string;
  after?: string;
  action?: string;
}

export interface DryRunResult {
  changeCount: number;
  dryRunId: string;
  dryRunHash: string;
  summary: string;
  /** A second line under the summary, such as how many records a run walked. */
  detail?: string;
  rows: SampleRow[];
  notes: string[];
  walkComplete: boolean;
  columns: 'action' | 'beforeAfter' | 'none';
}

export interface SystemScript {
  id: string;
  historyId: (options: Record<string, string | boolean>) => string;
  group: 'release' | 'normalization' | 'catalog';
  /** The group's title, shown above the name in the drawer. */
  groupTitle?: string;
  name: string;
  /** Full description shown in the drawer body. */
  description?: string;
  when: string;
  chips: Array<{ label: string }>;
  number?: number;
  applied: boolean;
  options: ScriptOption[];
  destructive: (options: Record<string, string | boolean>) => boolean;
  fallbackHistory?: { at: string | null; by: string; changeCount: number } | null;
  dryRun: (options: Record<string, string | boolean>, aborted: () => boolean) => Promise<DryRunResult>;
  apply: (options: Record<string, string | boolean>, receipt: { id: string; hash: string }, aborted: () => boolean) => Promise<void>;
}

/** The review opens on a short sample; Show all reveals the rest, up to this many. */
const SAMPLE_ROWS = 3;
const MAX_ROWS = 20;

const serialize = (options: Record<string, string | boolean>) =>
  JSON.stringify(Object.keys(options).sort().map((key) => [key, options[key]]));

export const RunDrawer: React.FC<{ script: SystemScript | null; onClose: () => void }> = ({ script, onClose }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const [options, setOptions] = useState<Record<string, string | boolean>>({});
  const [phase, setPhase] = useState<RunPhase>('idle');
  const [changeCount, setChangeCount] = useState(0);
  const [receipt, setReceipt] = useState<{ id: string; hash: string } | null>(null);
  const [receiptOptions, setReceiptOptions] = useState<string | null>(null);
  const [walkComplete, setWalkComplete] = useState(false);
  const [summary, setSummary] = useState('');
  const [detail, setDetail] = useState('');
  const [showAllRows, setShowAllRows] = useState(false);
  const [rows, setRows] = useState<SampleRow[]>([]);
  const [columns, setColumns] = useState<DryRunResult['columns']>('none');
  const [notes, setNotes] = useState<string[]>([]);
  const [done, setDone] = useState(false);
  const [runKind, setRunKind] = useState<'dry' | 'apply'>('dry');
  const abortRef = useRef(false);
  const phaseRef = useRef<RunPhase>('idle');
  const runGeneration = useRef(0);
  /** Generation of the request that currently owns `running`, if one does. */
  const activeRun = useRef<number | null>(null);

  const setRunPhase = (next: RunPhase) => {
    phaseRef.current = next;
    setPhase(next);
  };

  const dropReview = () => {
    setReceipt(null);
    setReceiptOptions(null);
    setChangeCount(0);
    setWalkComplete(false);
    setRows([]);
    setShowAllRows(false);
    setSummary('');
    setDetail('');
    setNotes([]);
    setDone(false);
  };

  useEffect(() => {
    if (!script) return;
    const initial: Record<string, string | boolean> = {};
    for (const option of script.options) initial[option.key] = option.defaultValue;
    const wasRunning = phaseRef.current === 'running';
    const cleared = clearReview(
      {
        phase: phaseRef.current,
        changeCount: 0,
        dryRunId: null,
        receiptOptions: null,
        options: '',
        walkComplete: false,
      },
      serialize(initial),
    );
    // Keep a request that is already in flight running, and make its result
    // unable to publish a review for the script now on screen.
    if (wasRunning) runGeneration.current += 1;
    else activeRun.current = null;
    setOptions(initial);
    setRunPhase(cleared.phase);
    setChangeCount(0);
    setReceipt(null);
    setReceiptOptions(null);
    setWalkComplete(false);
    setSummary('');
    setDetail('');
    setRows([]);
    setShowAllRows(false);
    setNotes([]);
    setDone(false);
    if (!wasRunning) abortRef.current = false;
  }, [script]);

  const queryClient = useQueryClient();
  const historyId = script ? script.historyId(options) : '';
  const history = useQuery({
    queryKey: ['admin-script-runs', historyId],
    enabled: Boolean(script && historyId),
    queryFn: () => systemJson<{ runs: Array<{ id: string; at: string | null; actorEmail: string | null; actor: string; changeCount: number }> }>(
      `/admin/system/script-runs?scriptId=${encodeURIComponent(historyId)}`,
    ),
  });

  const gate = {
    phase,
    changeCount,
    dryRunId: receipt?.id ?? null,
    receiptOptions,
    options: serialize(options),
    walkComplete,
  };
  const applyAllowed = canApply(gate);
  const destructive = script ? script.destructive(options) : false;

  const updateOption = (key: string, value: string | boolean) => {
    const next = { ...options, [key]: value };
    const previous = serialize(options);
    const nextSerialized = serialize(next);
    const cleared = afterOptionChange({ ...gate, options: previous }, nextSerialized);
    if (previous === nextSerialized) return;
    // Drop the review even while a request is in flight. The generation makes
    // that request's completion unable to publish a receipt or unlock Apply.
    if (phaseRef.current === 'running') runGeneration.current += 1;
    setOptions(next);
    setRunPhase(cleared.phase);
    dropReview();
  };

  const takeCompletion = (started: number): 'ignore' | 'superseded' | 'current' => {
    const outcome = classifyCompletion(started, runGeneration.current, activeRun.current);
    if (outcome !== 'ignore') activeRun.current = null;
    return outcome;
  };

  const runDry = async () => {
    if (!script || phaseRef.current === 'running') return;
    const started = ++runGeneration.current;
    activeRun.current = started;
    const usedOptions = serialize(options);
    abortRef.current = false;
    setRunKind('dry');
    setRunPhase('running');
    setDone(false);
    try {
      const result = await script.dryRun(options, () => abortRef.current);
      const outcome = takeCompletion(started);
      if (outcome === 'ignore') return;
      const settled = finishDryRun(started, runGeneration.current, {
        changeCount: result.changeCount,
        dryRunId: result.dryRunId || null,
        walkComplete: result.walkComplete,
        options: usedOptions,
      });
      if (outcome === 'superseded' || settled.phase !== 'reviewed') {
        setRunPhase('idle');
        return;
      }
      setChangeCount(settled.changeCount);
      setReceipt(result.dryRunId ? { id: result.dryRunId, hash: result.dryRunHash } : null);
      setReceiptOptions(settled.receiptOptions);
      setWalkComplete(settled.walkComplete);
      setSummary(result.summary);
      setDetail(result.detail || '');
      setRows(result.rows);
      setShowAllRows(false);
      setColumns(result.columns);
      setNotes(result.notes);
      setRunPhase('reviewed');
    } catch (error) {
      const outcome = takeCompletion(started);
      if (outcome === 'ignore') return;
      setRunPhase('idle');
      if (outcome === 'superseded') return;
      showToast((error as Error).message, 'error');
    }
  };

  const runApply = async () => {
    if (!script || !receipt || !applyAllowed || phaseRef.current === 'running') return;
    const started = runGeneration.current;
    activeRun.current = started;
    const receiptAtStart = receipt;
    const historyIdAtStart = historyId;
    abortRef.current = false;
    setRunKind('apply');
    setRunPhase('running');
    try {
      await script.apply(options, receiptAtStart, () => abortRef.current);
      const outcome = takeCompletion(started);
      if (outcome !== 'ignore') {
        const settled = finishApply(started, runGeneration.current, true);
        setRunPhase(settled.phase);
        if (settled.clearReceipt) {
          setReceipt(null);
          setReceiptOptions(null);
          setChangeCount(0);
        }
        // The banner belongs to the options on screen. A superseded apply wrote
        // the previous ones, so it must not look like this review was applied.
        if (outcome === 'current') setDone(true);
      }
      // The write already landed. Refresh the history captured when Apply was
      // clicked. The observer may since have moved to another script.
      showToast(t('admin.systemV2.scripts.applied'), 'success');
      void queryClient.invalidateQueries({ queryKey: ['admin-script-runs', historyIdAtStart] });
    } catch (error) {
      const outcome = takeCompletion(started);
      if (outcome !== 'ignore') {
        const settled = finishApply(started, runGeneration.current, false);
        setRunPhase(settled.phase);
        if (settled.clearReceipt) {
          setReceipt(null);
          setReceiptOptions(null);
          setChangeCount(0);
        }
      }
      showToast((error as Error).message, 'error');
    }
  };

  const steps = ['dry', 'review', 'apply'] as const;
  // 1 = dry run next, 2 = reviewing a result, 3 = applied.
  const stepIndex = done ? 3 : phase === 'reviewed' ? 2 : 1;
  const runs = history.data?.runs || [];
  const reviewed = phase === 'reviewed' || (phase === 'running' && Boolean(summary));
  const resultTone = done ? tokens.signal.positive : changeCount === 0 ? tokens.signal.neutral : destructive ? tokens.signal.problem : tokens.signal.info;
  const footNote = done
    ? t('admin.systemV2.scripts.footApplied')
    : phase === 'reviewed'
      ? changeCount
        ? t('admin.systemV2.scripts.footReady')
        : t('admin.systemV2.scripts.footNothing')
      : t('admin.systemV2.scripts.footLocked');

  const optionFields = useMemo(() => script?.options || [], [script]);
  const revealableRows = Math.min(rows.length, MAX_ROWS);
  const sampleRows = rows.slice(0, showAllRows ? MAX_ROWS : SAMPLE_ROWS);

  const overline = { fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.muted } as const;

  return (
    <Drawer anchor="right" open={Boolean(script)} onClose={onClose} PaperProps={{ sx: { width: 'min(560px, 100vw)' } }}>
      {script && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2.75, py: 2.25, borderBottom: `1px solid ${tokens.line.hairline}` }}>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {script.groupTitle && <Typography sx={{ ...overline, fontSize: 11.5 }}>{script.groupTitle}</Typography>}
              <Typography component="h2" sx={{ fontWeight: 700, fontSize: 17, overflowWrap: 'anywhere' }}>{script.name}</Typography>
              <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{script.when}</Typography>
            </Box>
            <IconButton aria-label={t('admin.systemV2.scripts.close')} onClick={onClose} size="small" sx={{ border: `1px solid ${tokens.line.default}`, borderRadius: `${tokens.radius.small}px` }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2.25, px: 2.75, py: 2.25, '& > *': { flexShrink: 0 } }}>
            {script.description && (
              <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: tokens.ink.body }}>{script.description}</Typography>
            )}
            {script.chips.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {script.chips.map((chip) => (
                  <StatusChip
                    key={chip.label}
                    tone="neutral"
                    label={chip.label}
                    sx={chip.label.includes('/') || chip.label.endsWith('.ts') ? { fontFamily: tokens.font.mono, fontSize: 11.5 } : undefined}
                  />
                ))}
              </Box>
            )}

            <Box component="ol" aria-label={t('admin.systemV2.scripts.stepsLabel')} sx={{ display: 'flex', alignItems: 'center', gap: 1, listStyle: 'none', m: 0, p: 0 }}>
              {steps.map((step, index) => {
                const n = index + 1;
                const complete = n < stepIndex || done;
                const current = n === stepIndex && !done;
                return (
                  <Box component="li" key={step} aria-current={current ? 'step' : undefined} sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 0', minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        flex: '0 0 auto',
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: complete ? tokens.signal.positive.solid : current ? tokens.accent.main : tokens.line.default,
                        color: complete || current ? tokens.ink.inverse : tokens.ink.muted,
                      }}
                    >
                      {complete ? '✓' : n}
                    </Box>
                    <Typography component="span" sx={{ fontSize: 12.5, fontWeight: current ? 600 : 500, color: current || complete ? tokens.ink.primary : tokens.ink.muted, whiteSpace: 'nowrap' }}>
                      {t(`admin.systemV2.scripts.steps.${step}`)}
                    </Typography>
                    {index < steps.length - 1 && <Box component="span" sx={{ flex: 1, height: '1px', bgcolor: tokens.line.default, minWidth: 8 }} />}
                  </Box>
                );
              })}
            </Box>

            {optionFields.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2, border: `1px solid ${tokens.line.default}`, borderRadius: `${tokens.radius.control}px` }}>
                <Typography sx={overline}>{t('admin.systemV2.scripts.options')}</Typography>
                {optionFields.map((option) => (
                  <Box key={option.key}>
                    {option.type === 'boolean' ? (
                      <FormControlLabel
                        control={<Switch checked={Boolean(options[option.key])} onChange={(event) => updateOption(option.key, event.target.checked)} />}
                        label={
                          <Box>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{option.label}</Typography>
                            {option.help && <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{option.help}</Typography>}
                          </Box>
                        }
                        sx={{ m: 0, alignItems: 'flex-start', gap: 1 }}
                      />
                    ) : option.type === 'select' ? (
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={option.label}
                        value={String(options[option.key] ?? '')}
                        onChange={(event) => updateOption(option.key, event.target.value)}
                        // An empty value is a real choice ("Any brand"), so the select shows it.
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{ displayEmpty: true }}
                      >
                        {(option.choices || []).map((choice) => (
                          <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <TextField size="small" fullWidth label={option.label} value={String(options[option.key] ?? '')} onChange={(event) => updateOption(option.key, event.target.value)} helperText={option.help} />
                    )}
                  </Box>
                ))}
                <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{t('admin.systemV2.scripts.optionsReset')}</Typography>
              </Box>
            )}

            {destructive && <StatusChip tone="problem" label={t('admin.systemV2.scripts.overwrites')} sx={{ alignSelf: 'flex-start' }} />}

            {phase === 'running' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, border: `1px solid ${tokens.line.default}`, borderRadius: `${tokens.radius.control}px`, bgcolor: tokens.surface.sunken }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {runKind === 'apply' ? t('admin.systemV2.scripts.applying') : t('admin.systemV2.scripts.dryRunning')}
                </Typography>
                <LinearProgress sx={{ height: 6, borderRadius: 3, bgcolor: tokens.line.default, '& .MuiLinearProgress-bar': { bgcolor: tokens.accent.main } }} />
                <Button size="small" onClick={() => { abortRef.current = true; }} sx={{ alignSelf: 'flex-start' }}>
                  {t('admin.systemV2.scripts.stop')}
                </Button>
              </Box>
            )}

            {(reviewed || done) && summary && (
              <Box sx={{ border: `1px solid ${resultTone.border}`, borderRadius: `${tokens.radius.control}px`, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.375, px: 2, py: 1.5, bgcolor: resultTone.tint }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: resultTone.ink }}>
                    {done ? t('admin.systemV2.scripts.applied') : changeCount === 0 ? t('admin.systemV2.scripts.nothing') : summary}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: tokens.ink.bodyAlt }}>
                    {done
                      ? t('admin.systemV2.scripts.appliedSub')
                      : changeCount === 0
                        ? t('admin.systemV2.scripts.nothingSub')
                        : sampleRows.length > 0 && columns !== 'none' && changeCount > sampleRows.length
                          ? t('admin.systemV2.scripts.dryRunSubMore', { count: sampleRows.length })
                          : t('admin.systemV2.scripts.dryRunSub')}
                  </Typography>
                </Box>
                {!done && detail && (
                  <Typography sx={{ px: 2, py: 1, fontSize: 12.5, color: tokens.ink.muted, borderTop: `1px solid ${tokens.line.hairline}` }}>{detail}</Typography>
                )}
                {notes.map((note) => (
                  <Typography key={note} sx={{ px: 2, py: 1, fontSize: 12.5, color: tokens.ink.muted, borderTop: `1px solid ${tokens.line.hairline}` }}>{note}</Typography>
                ))}
                {!done && sampleRows.length > 0 && columns !== 'none' && (
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 12 }}>
                    <Box component="thead">
                      <Box component="tr">
                        {(columns === 'beforeAfter' ? ['record', 'field', 'before', 'after'] : ['record', 'field', 'action']).map((column) => (
                          <Box key={column} component="th" sx={{ ...overline, fontSize: 11, textAlign: 'left', px: 2, py: 1, borderTop: `1px solid ${tokens.line.hairline}` }}>
                            {t(`admin.systemV2.scripts.sample.${column}`)}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {sampleRows.map((row) => (
                        <Box component="tr" key={row.id}>
                          <Box component="td" sx={{ px: 2, py: 1, borderTop: `1px solid ${tokens.line.hairline}`, fontFamily: tokens.font.mono, fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.id}>{row.id}</Box>
                          <Box component="td" sx={{ px: 2, py: 1, borderTop: `1px solid ${tokens.line.hairline}`, color: tokens.ink.bodyAlt, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.title}>{row.title}</Box>
                          {columns === 'beforeAfter' ? (
                            <>
                              <Box component="td" sx={{ px: 2, py: 1, borderTop: `1px solid ${tokens.line.hairline}`, color: tokens.ink.faint, textDecoration: 'line-through', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.before || '—'}</Box>
                              <Box component="td" sx={{ px: 2, py: 1, borderTop: `1px solid ${tokens.line.hairline}`, color: tokens.signal.positive.ink, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.after || '—'}</Box>
                            </>
                          ) : (
                            <Box component="td" sx={{ px: 2, py: 1, borderTop: `1px solid ${tokens.line.hairline}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.action}</Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                {!done && columns !== 'none' && rows.length > SAMPLE_ROWS && (
                  <Box sx={{ px: 1, py: 0.5, borderTop: `1px solid ${tokens.line.hairline}` }}>
                    <Button size="small" aria-expanded={showAllRows} onClick={() => setShowAllRows((value) => !value)}>
                      {showAllRows
                        ? t('admin.systemV2.scripts.showFewer', { count: SAMPLE_ROWS })
                        : rows.length > MAX_ROWS
                          ? t('admin.systemV2.scripts.showFirst', { count: revealableRows })
                          : t('admin.systemV2.scripts.showAll', { count: revealableRows })}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography sx={overline}>{t('admin.systemV2.scripts.history')}</Typography>
              {runs.length === 0 && script.fallbackHistory ? (
                <HistoryRow
                  at={asDate(script.fallbackHistory.at)}
                  text={t('admin.systemV2.scripts.historyLine', { who: script.fallbackHistory.by, count: script.fallbackHistory.changeCount })}
                />
              ) : runs.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: tokens.ink.faint }}>{t('admin.systemV2.scripts.neverRun')}</Typography>
              ) : (
                runs.slice(0, 8).map((run) => (
                  <HistoryRow key={run.id} at={asDate(run.at)} text={t('admin.systemV2.scripts.historyLine', { who: run.actorEmail || run.actor, count: run.changeCount })} />
                ))
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', px: 2.75, py: 1.75, borderTop: `1px solid ${tokens.line.default}`, bgcolor: tokens.surface.subtle }}>
            <Typography sx={{ flex: '1 1 180px', minWidth: 0, fontSize: 12, color: tokens.ink.muted }}>{footNote}</Typography>
            <Button variant="outlined" disabled={phase === 'running'} onClick={runDry}>{t('admin.systemV2.scripts.dryRun')}</Button>
            <Button
              variant="contained"
              color={destructive ? 'error' : 'primary'}
              disabled={!applyAllowed || phase === 'running'}
              onClick={runApply}
            >
              {applyAllowed ? t('admin.systemV2.scripts.applyCount', { count: changeCount }) : t('admin.systemV2.scripts.apply')}
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

const HistoryRow: React.FC<{ at: Date | null; text: string }> = ({ at, text }) => (
  <Box sx={{ display: 'flex', gap: 1.25, fontSize: 12.5, color: tokens.ink.bodyAlt, flexWrap: 'wrap' }}>
    <Box component="span" sx={{ flex: '0 0 140px', color: tokens.ink.muted }}>
      {at ? at.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
    </Box>
    <span>{text}</span>
  </Box>
);
